import Link from '@pinecast/common/Link';
import Select from '@pinecast/common/Select';
import type {IntlShape} from '@pinecast/i18n';
import {FormattedMessage, useIntl} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import * as React from 'react';

type Props = {
  index: number;
  speaker: string;
  speakerMap?: Record<string, string>;
  allSpeakerLabels: Array<string>;
  showSpeakerLabels: boolean;
  onEdit?: (speaker: string, index: number) => Promise<void>;
  canMergeWithPrevious?: boolean;
  onMerge?: () => Promise<void>;
};

function formatSpeaker(
  letter: string,
  speakerMap: Record<string, string> | undefined,
  showSpeakerLabels: boolean,
  intl: IntlShape,
  addSuffix: string = ':',
) {
  const character = speakerMap?.[letter];
  if (character) {
    if (showSpeakerLabels && character !== letter) {
      return intl.formatMessage(
        {
          description:
            'Label for the speaker in the transcript when speakers are annotated with names. {letter} is the letter of the alphabet corresponding to the speaker. {name} is the name of the speaker. {suffix} is an optional colon or other punctuation.',
          defaultMessage: '{name} ({letter}){suffix}',
        },
        {
          letter,
          name: character,
          suffix: addSuffix,
        },
      );
    }
    return character;
  } else if (letter.length > 1) {
    return letter;
  }
  return intl.formatMessage(
    {
      description:
        'Default label for the speaker in the transcript when speakers are not annotated with names. {letter} is the letter of the alphabet corresponding to the speaker. {suffix} is an optional colon or other punctuation.',
      defaultMessage: 'Speaker {letter}{suffix}',
    },
    {letter, suffix: addSuffix},
  );
}

function unusedLetter(usedLetters: Array<string>) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  for (const letter of letters) {
    if (!usedLetters.includes(letter)) {
      return letter;
    }
  }
  return null;
}

const ChangePendingSelect = ({
  value,
  onChange,
  speakerMap,
  allSpeakerLabels,
}: {
  value: string;
  onChange: (value: string) => Promise<void>;
  speakerMap?: Record<string, string>;
  allSpeakerLabels: Array<string>;
}) => {
  const intl = useIntl();
  const [pending, setPending] = React.useState(false);

  const newLetter = unusedLetter(allSpeakerLabels);
  const labels = React.useMemo(
    () =>
      allSpeakerLabels.sort().map(letter => ({
        key: letter,
        label: formatSpeaker(letter, speakerMap, true, intl, ''),
      })),
    [allSpeakerLabels, speakerMap, intl],
  );
  return (
    <Select
      disabled={pending}
      value={value}
      onChange={newSpeaker => {
        setPending(true);
        onChange(newSpeaker).finally(() => setPending(false));
      }}
      options={
        newLetter
          ? labels.concat([
              {
                key: newLetter,
                label: intl.formatMessage(
                  {
                    description:
                      'Label for the new speaker option in the transcript when speakers are annotated with names. {letter} is the letter of the alphabet corresponding to the speaker.',
                    defaultMessage: 'New speaker ({letter})',
                  },
                  {
                    letter: newLetter,
                  },
                ),
              },
            ])
          : labels
      }
    />
  );
};

const MergeLink = ({onMerge}: {onMerge: () => Promise<void>}) => {
  const [pending, setPending] = React.useState(false);
  const handleClick = React.useCallback(() => {
    setPending(true);
    onMerge().finally(() => setPending(false));
  }, [onMerge]);
  return (
    <Link onClick={handleClick} disabled={pending}>
      <FormattedMessage
        description="Link text to merge this utterance with the one above it"
        defaultMessage="Merge with above"
      />
    </Link>
  );
};

const TranscriptUtteranceSpeakerEditor = ({
  speaker,
  speakerMap,
  allSpeakerLabels,
  showSpeakerLabels,
  index,
  onEdit,
  canMergeWithPrevious,
  onMerge,
}: Props) => {
  const css = useCSS();
  const intl = useIntl();
  const handleChange = React.useCallback(
    (value: string) => {
      if (value !== speaker && onEdit) {
        return onEdit(value, index);
      }
      return Promise.resolve();
    },
    [speaker, index, onEdit],
  );
  return onEdit ? (
    <div
      className={css({display: 'flex', flexDirection: 'column', gap: '4px'})}
    >
      <ChangePendingSelect
        value={speaker}
        onChange={handleChange}
        allSpeakerLabels={allSpeakerLabels}
        speakerMap={speakerMap}
      />
      {canMergeWithPrevious && onMerge && <MergeLink onMerge={onMerge} />}
    </div>
  ) : (
    <span
      className={css({
        fontWeight: 500,
        whiteSpace: 'nowrap',
        fontFamily: 'Monument Grotesk Bold',
        textAlign: 'right',
      })}
    >
      {formatSpeaker(speaker, speakerMap, !!showSpeakerLabels, intl)}
    </span>
  );
};

export default TranscriptUtteranceSpeakerEditor;
